/**
 * Shared editorial content for the Villas Pura Vida Guest Experience.
 * Used by both the public Experiencia page and the private Guest Portal.
 * Drafts based on: Documento Cero, Prólogo, Manual Airbnb Casita.
 */

export const HERO_QUOTE = "Gracias por elegir este lugar para crear nuevos recuerdos.";

export const HERO_QUOTE_PUBLIC = "Un lugar donde las familias bajan el ritmo, conviven cerca del mar y construyen recuerdos que dan ganas de volver.";

/* -----------------------------------------------------------
 * The Story — public + private
 * ----------------------------------------------------------- */
export const HISTORIA = {
  eyebrow: "EL LEGADO",
  title: "Un padre. Un mar. Un recuerdo.",
  body: [
    "El dueño de Villas Pura Vida es el mayor de nueve hermanos. Durante su infancia, su padre —con recursos limitados— llevaba a los nueve al mar de Tuxpan una vez al año. No había hoteles, ni vacaciones largas, ni casas de descanso. Solo un objetivo: que sus hijos conocieran el mar y guardaran ese recuerdo para siempre.",
    "Muchos años después, uno de aquellos niños regresó a esta costa junto con dos de sus hermanos y construyeron tres casas sencillas. Con el tiempo, cada familia tomó su propio camino. El hermano mayor renovó su espacio, abrió La Casita y después construyó El Loft.",
    "Hoy comparte con otras familias el lugar que primero fue refugio y memoria para la suya.",
  ],
  quote: "No quiso comprar una casa de playa. Quiso recuperar un recuerdo.",
};

/* -----------------------------------------------------------
 * The Place — inspiration, not instruction
 * ----------------------------------------------------------- */
export const LUGAR = {
  eyebrow: "DESCUBRE EL LUGAR",
  title: "No instrucciones. Inspiración.",
  items: [
    { icon: "waves", label: "La playa", body: "Semivirgen, amplia, con días de muy baja afluencia. El mar como escenario, no como decorado." },
    { icon: "sun", label: "Amaneceres y atardeceres", body: "El amanecer se ve desde la playa — el sol sale entre las 5:50 y las 6:15 am en verano. El atardecer se ve del lado del río, pero todo el cielo se pinta de colores." },
    { icon: "umbrella-beach", label: "El asoleadero", body: "Palapa con hamacas cerca del mar. A veces con mesa y sillas para desayunar sin prisa." },
    { icon: "fire", label: "La fogata", body: "Área específica entre el asoleadero y el arco. El ritual del atardecer." },
    { icon: "utensils", label: "El asador", body: "Palapa de ~10×5 m después del arco. Carne asada familiar, coordinada cuando hay otros huéspedes." },
    { icon: "route", label: "El río", body: "A unos 200 metros caminando por el terreno baldío. Un paseo opcional al atardecer." },
    { icon: "person-hiking", label: "Caminatas", body: "La playa se extiende en ambas direcciones. Caminar sin destino es parte de la experiencia." },
  ],
};

/* -----------------------------------------------------------
 * How to arrive — visual references
 * ----------------------------------------------------------- */
export const LLEGADA = {
  eyebrow: "CÓMO LLEGAR",
  title: "La llegada es parte del viaje.",
  intro: "Los últimos 15 a 20 minutos del recorrido se realizan por la playa. Para muchos huéspedes, esa carretera de arena es el inicio de las vacaciones.",
  references: [
    { step: "01", label: "Parque acuático", body: "A la izquierda del camino." },
    { step: "02", label: "Hotel Boutique Tajín", body: "Referencia clave — continúa recto." },
    { step: "03", label: "Arco blanco con rayas azules", body: "Aparece aproximadamente 3 minutos después del Tajín. Marca la llegada." },
    { step: "04", label: "Entrada al conjunto", body: "No hay portón general. El arco es la señal." },
  ],
  weather: "Si llovió: revisa la marea antes de recorrer los últimos minutos por la playa. Si te atoras, llámanos, el matrimonio cuidador ayuda con la maniobra.",
};

/* -----------------------------------------------------------
 * Local recommendations
 * ----------------------------------------------------------- */
export const RECOMENDACIONES = {
  eyebrow: "RECOMENDACIONES LOCALES",
  title: "Antes o durante tu estancia.",
  categories: [
    { icon: "fish", label: "Mariscos y pescado", body: "Consulta con nosotros según lo que busques — te decimos dónde compran fresco los locales." },
    { icon: "mug-saucer", label: "Café y desayunos", body: "Opciones en Tuxpan antes de llegar a Barra de Galindo." },
    { icon: "cart-shopping", label: "Provisiones", body: "Supermercado y tiendas para hielo, garrafón, carbón, pizza o pollo antes de llegar." },
    { icon: "gas-pump", label: "Gasolinera", body: "Última gasolinera antes del camino por la playa." },
    { icon: "kit-medical", label: "Farmacia", body: "Farmacia disponible en Tuxpan para lo esencial." },
  ],
  comidaPreparada: {
    icon: "utensils",
    label: "¿Y si no quieren cocinar?",
    body:
      "Los encargados del conjunto **conocen los lugares locales donde se puede comprar comida**. Si gustan, ellos consultan qué hay en el menú del día y **se las traen por un costo adicional** que se coordina directamente con ellos por WhatsApp — no preparan comida, hacen el mandado para ustedes.",
  },
  salidaTuxpan: {
    icon: "map-location-dot",
    label: "Salir a desayunar o comer a Tuxpan",
    body:
      "Tuxpan queda a **media hora** aproximadamente desde la casa (por el camino de playa). Es una excursión corta y estos son los **5 lugares que recomiendan los locales**:",
    lugares: [
      {
        n: "01",
        name: "Bocoles y Empanadas Las Tías",
        subtitle: "Desayuno como local",
        desc: "Bocoles, empanadas y antojitos huastecos.",
        rating: "4.6 · ~600 reseñas",
        address: "Av. Cuauhtémoc 81, Centro, Tuxpan, Ver.",
      },
      {
        n: "02",
        name: "Restaurante Chilolos",
        subtitle: "Mariscos tradicionales",
        desc: "Pescados y mariscos frescos con sazón de casa.",
        rating: "4.3 · ~600 reseñas",
        address: "Calle Garizurieta 304, Centro, Tuxpan, Ver.",
      },
      {
        n: "03",
        name: "La Mata",
        subtitle: "La experiencia de la laguna",
        desc: "Pescados y mariscos con vista a la Laguna de Tampamachoco.",
        rating: "4.4 · +1,300 reseñas",
        address: "Ribera del Pescador S/N, La Mata, Tuxpan, Ver.",
      },
      {
        n: "04",
        name: "El Mangle · Mariscos",
        subtitle: "Fresco, casual y delicioso",
        desc: "Mariscos frescos en un ambiente relajado y familiar.",
        rating: "4.3 · ~500 reseñas",
        address: "Carretera a la Barra Nte. km 6, La Mata, Tuxpan, Ver.",
      },
      {
        n: "05",
        name: "Restaurante Nuevo 303",
        subtitle: "Comida mexicana de siempre",
        desc: "Comida mexicana clásica, rica y económica.",
        rating: "4.2 · +2,100 reseñas",
        address: "Calle 2 de Abril 303, Centro, Tuxpan, Ver.",
      },
    ],
    tip: "**Tip local:** La Mata de Tampamachoco no es solo un lugar para comer, es todo un paseo: buena comida, vista a la laguna y la brisa del mar. Vengan, prueben y descúbranlo.",
  },
};

/* -----------------------------------------------------------
 * Parque acuático — AquaPark Tuxpan (referencia local)
 * ----------------------------------------------------------- */
export const AQUAPARK = {
  eyebrow: "CERCA DEL CONJUNTO",
  title: "AquaPark Tuxpan.",
  intro:
    "Un parque acuático familiar sobre la playa de Barra Norte — a pocos minutos por el camino de playa desde Villas Pura Vida. Buena opción para un día distinto sin salir de la zona.",
  quickFacts: [
    { icon: "location-dot", label: "Ubicación",  value: "Carretera Tuxpan, Barra Norte — a 1 km de la termoeléctrica, frente a Barra Galindo." },
    { icon: "route",        label: "Distancia",  value: "A pocos minutos desde Villas Pura Vida por el camino de playa." },
    { icon: "clock",        label: "Horarios",   value: "Jueves a domingo · 10:00 am – 6:00 pm." },
  ],
  prices: {
    icon: "ticket",
    label: "Precios de referencia",
    items: [
      { label: "Adultos",                          value: "$180" },
      { label: "Niños",                            value: "$120" },
      { label: "INAPAM",                           value: "$120" },
      { label: "Pase anual · individual",          value: "$600" },
      { label: "Pase anual · 5 personas",          value: "$2,500" },
    ],
  },
  features: [
    { icon: "person-swimming", label: "Toboganes y albercas familiares" },
    { icon: "umbrella-beach", label: "A pie de playa · brisa del mar" },
    { icon: "utensils", label: "Área de alimentos y sombras" },
    { icon: "child", label: "Ideal para ir con niños" },
  ],
  disclaimer:
    "Horarios y precios son de referencia (fuentes turísticas locales). Antes de ir, escríbenos por WhatsApp para confirmar que esté abierto ese día y los precios actualizados.",
};

/* -----------------------------------------------------------
 * Public FAQ
 * ----------------------------------------------------------- */
export const FAQ_PUBLIC = [
  {
    q: "¿Está frente al mar?",
    a: "La playa se encuentra a unos pasos caminando. La casa no tiene vista directa al mar desde su interior, pero el acceso es muy cercano y forma parte de una playa semivirgen con muy poca afluencia.",
  },
  {
    q: "¿Es una playa privada?",
    a: "No. La playa es pública, pero suele ser muy tranquila y con poca afluencia, lo que permite disfrutar de una experiencia muy relajada.",
  },
  {
    q: "¿Aceptan mascotas?",
    a: "Sí — hasta 4 mascotas por reserva. Por respeto al mobiliario y descanso de otros huéspedes, duermen en el exterior de la casa. Avísanos con anticipación para prepararte todo mejor.",
  },
  {
    q: "¿Cómo es el acceso?",
    a: "El último tramo del recorrido (15–20 minutos) se realiza conduciendo por la playa. Es una experiencia muy característica de Barra de Galindo. Te enviaremos indicaciones detalladas antes de tu llegada.",
  },
  {
    q: "¿Hay WiFi?",
    a: "Sí. Compartiremos la información de acceso al confirmar tu reserva.",
  },
  {
    q: "¿Hay tienda o supermercado cerca?",
    a: "En Barra de Galindo hay solo una tienda pequeña que no está bien surtida. El supermercado más cercano está en Tuxpan, a unos 30 minutos por el camino de playa. Recomendamos hacer la compra fuerte antes de tomar la playa. Si algo se olvida, la persona que cuida el conjunto puede hacer mandados desde $100 pesos por viaje, según la distancia y la cantidad de cosas a traer.",
  },
  {
    q: "¿Hay restaurantes cerca?",
    a: "Sí. Podemos recomendarte opciones según el tipo de comida que busques y también lugares para comprar provisiones antes de llegar.",
  },
  {
    q: "¿Puedo llegar en cualquier automóvil?",
    a: "Sí, la mayoría de nuestros huéspedes llega en automóviles particulares sin inconvenientes. Si viajas en un vehículo con suspensión muy baja o deportiva, avísanos antes de tu llegada y te damos recomendaciones específicas según el estado del camino.",
  },
  {
    q: "¿Tiene vista al mar desde el interior?",
    a: "El Loft no tiene vista directa al mar desde el interior — al salir das unos pasos y ya lo puedes observar. La Casita tampoco tiene vista directa, pero la playa está a un par de minutos caminando. El valor está en la tranquilidad del entorno y en la cercanía a una playa semivirgen.",
  },
];

/* -----------------------------------------------------------
 * Private FAQ — for confirmed guests
 * ----------------------------------------------------------- */
export const FAQ_PRIVATE = [
  {
    q: "¿Puedo llegar en cualquier automóvil?",
    a: "Sí, la mayoría de nuestros huéspedes llega en automóviles particulares sin inconvenientes. Si viajas en un vehículo con suspensión muy baja o deportiva, avísanos antes para darte recomendaciones específicas según las condiciones del camino.",
  },
  {
    q: "¿Y si llegamos después de las 8 pm?",
    a: "La hora máxima de llegada recomendada es a las 8:00 pm. Después de esa hora, el acceso de los últimos 10 minutos —que es solo por playa— se vuelve complicado. De cualquier forma siempre estaremos a tus órdenes; si algo se atraviesa en el camino, contáctanos.",
  },
  {
    q: "¿Qué pasa si llueve intensamente antes de mi viaje?",
    a: "Escríbenos antes de salir de casa. Te contamos cómo están las condiciones del camino de playa y te damos las mejores recomendaciones.",
  },
  {
    q: "¿Pueden hacer mandados si algo se me olvida?",
    a: "Sí. La persona que cuida el conjunto puede ir por víveres, hielo u otros pendientes. El costo empieza en $100 pesos por viaje y varía según la distancia y la cantidad de cosas a traer. Coordina por WhatsApp con anticipación para que tu día no se detenga.",
  },
  {
    q: "¿Dónde hay supermercado?",
    a: "En Tuxpan encontrarás varios supermercados y tiendas. Te recomendamos hacer compras antes de tomar el camino por la playa para no cargar hasta allá. En Barra de Galindo solo hay una tienda pequeña, útil para emergencias pero no está bien surtida.",
  },
  {
    q: "¿Dónde puedo comprar hielo?",
    a: "En las tiendas de conveniencia de Tuxpan, o en la última gasolinera antes del camino por la playa. Suele venderse en bolsas grandes.",
  },
  {
    q: "¿Dónde venden pescado fresco?",
    a: "En el mercado de Tuxpan o directamente con los pescadores locales. Avísanos y te podemos recomendar el mejor lugar según el día.",
  },
  {
    q: "¿Qué pasa si falla la luz?",
    a: "Las fallas eléctricas son ocasionales por las condiciones de la zona costera. Normalmente vuelven en poco tiempo. Si algo persiste, el matrimonio cuidador te avisa y te apoya.",
  },
  {
    q: "¿Dónde está el río?",
    a: "Se encuentra aproximadamente a 200 metros caminando desde los alojamientos, atravesando el terreno baldío. Es un paseo opcional muy tranquilo al atardecer.",
  },
  {
    q: "¿Cómo pido apoyo si algo pasa?",
    a: "Un matrimonio vive en un departamento independiente dentro del conjunto y cuida el lugar. Puedes escribirnos por WhatsApp en cualquier momento.",
  },
  {
    q: "¿Y si olvido algo al salir?",
    a: "Escríbenos lo antes posible. Verificamos si fue localizado y coordinamos el envío (los gastos de paquetería quedan a cargo del huésped).",
  },
];

/* -----------------------------------------------------------
 * How to enjoy better — positive tips (not warnings)
 * ----------------------------------------------------------- */
export const CONSEJOS = {
  eyebrow: "CÓMO DISFRUTAR MEJOR",
  title: "Pequeños consejos, no advertencias.",
  tips: [
    "Si desayunas temprano en el asoleadero, suele haber muy pocos mosquitos y la luz es la más suave del día.",
    "Al atardecer, si sopla viento, es el momento ideal para caminar por la playa hacia el sur.",
    "Encender la fogata funciona mejor con la madera que dejamos en el área asignada; usa el mismo lugar cada noche.",
    "Coordinar el uso del asador con anticipación (a través de WhatsApp) te asegura tenerlo cuando lo necesites.",
    "Al anochecer salen los cangrejos azules — un espectáculo local. En los últimos años sus avistamientos han disminuido y están en peligro de extinción; si los ves, admíralos sin tocarlos.",
  ],
};

/* -----------------------------------------------------------
 * Nature — mosquitos, wind, electricity (contextualized, not alarmed)
 * ----------------------------------------------------------- */
export const NATURALEZA = {
  eyebrow: "ESTAMOS EN UNA PLAYA SEMIVIRGEN",
  title: "Naturaleza, no problema.",
  intro: "El valor de este lugar es también su naturaleza. Compartimos algunos consejos prácticos, no advertencias.",
  criticalNote: "Repelente y plaquitas son esenciales. Si no los traen, la estancia se vuelve muy incómoda por los piquetes.",
  items: [
    { icon: "spray-can", label: "Repelente", body: "Traer un repelente cómodo para toda la familia. Los aplicas antes del atardecer, no cuando ya hay mosquitos.", critical: true },
    { icon: "shirt", label: "Ropa ligera de manga larga", body: "Para el atardecer al aire libre — la brisa suele ser fresca y protege de los mosquitos." },
    { icon: "wind", label: "El viento", body: "Cuando sopla, los mosquitos desaparecen. Suele soplar más al medio día y al atardecer." },
    { icon: "fire", label: "Plaquitas y espirales", body: "Plaquitas para interiores. Espirales solo en exteriores, lejos de niños, mascotas o materiales inflamables.", critical: true },
    { icon: "bolt", label: "Suministro eléctrico", body: "Puede tener variaciones ocasionales por las condiciones de la zona. Rara vez dura mucho — normalmente vuelve solo. Si la luz llega a faltar, el matrimonio cuidador se acercará a ustedes para prender la planta eléctrica y darles instrucciones sobre su uso." },
  ],
};

/* -----------------------------------------------------------
 * Antes de venir — CRITICAL. Barra de Galindo tiene solo una
 * tienda pequeña mal surtida. El supermercado real está a 30 min.
 * ----------------------------------------------------------- */
export const PROVISIONES = {
  eyebrow: "ANTES DE VENIR",
  title: "Traigan sus víveres desde casa.",
  criticalNote:
    "Estamos en zona semivirgen. La tienda cercana es pequeña y no está bien surtida. El supermercado más cercano está a 30 minutos. Vengan preparados — hará su estancia mucho más cómoda.",
  bring: {
    icon: "basket-shopping",
    label: "Lo que sí o sí conviene traer",
    items: [
      "**Garrafón de agua (o botellas grandes).**",
      "**Alimentos y víveres para toda la estancia.**",
      "Bebidas para adultos y niños.",
      "Hielo — llegar con hielera preparada.",
      "Botanas, café, azúcar, sal y aceite.",
      "Carbón si van a usar el asador.",
      "**Repelente para mosquitos** — aplicar antes de salir de la casa; en ocasiones hay desde la mañana.",
      "Crema o gel para picaduras — por si acaso.",
      "**Plaquitas para interiores y espirales para exteriores.**",
      "Protector solar y ropa ligera de manga larga.",
    ],
  },
  distances: {
    icon: "location-dot",
    label: "Sobre las tiendas cercanas",
    items: [
      "Barra de Galindo: hay una tienda pequeña, útil para emergencias pero no está bien surtida.",
      "Tuxpan: supermercados y todo lo demás — a aproximadamente 30 minutos por el camino de playa.",
      "Recomendación: hacer la compra fuerte en Tuxpan antes de tomar la playa.",
    ],
  },
  mandados: {
    icon: "cart-shopping",
    label: "Servicio de mandados",
    price: "Desde $100",
    unit: "por viaje",
    body:
      "Si algo se olvidó o se acabó, la persona que cuida el conjunto puede hacer el mandado por ustedes. El costo empieza en $100 pesos por viaje y varía según la distancia y la cantidad de cosas a traer. Coordinen por WhatsApp con anticipación.",
  },
};

/* -----------------------------------------------------------
 * Public teaser cards for /experiencia
 * ----------------------------------------------------------- */
export const PUBLIC_CARDS = [
  { icon: "heart", label: "La historia", body: "El recuerdo que dio origen a este lugar.", href: "#historia" },
  { icon: "water", label: "Descubre el lugar", body: "Playa, asoleadero, fogata, asador y río.", href: "#lugar" },
  { icon: "map-location-dot", label: "Cómo llegar", body: "El último tramo se hace por la playa.", href: "#llegar" },
  { icon: "house-chimney", label: "Amenidades", body: "Cocina, aire, alberca familiar y más.", href: "#amenidades" },
  { icon: "utensils", label: "Recomendaciones", body: "Mariscos, café, tiendas y farmacia.", href: "#recomendaciones" },
  { icon: "circle-question", label: "Preguntas frecuentes", body: "Todo lo que quieres saber antes de reservar.", href: "#faq" },
];

/* -----------------------------------------------------------
 * Brand pastels — soft solid backgrounds for cards.
 * Each card carries its own accent + tinted background.
 * ----------------------------------------------------------- */
const PASTEL_TERRA  = "#F3E4DB"; // terracota tenue
const PASTEL_SAND   = "#F7EED8"; // arena/dorado tenue
const PASTEL_SAGE   = "#E4EDE7"; // verde salvia tenue
const PASTEL_MAR    = "#E5EAF0"; // azul mar tenue

const ACCENT_TERRA  = "#E07A5F";
const ACCENT_GOLD   = "#B8873B";
const ACCENT_SAGE   = "#5F8B76";
const ACCENT_MAR    = "#3D5A80";

/* -----------------------------------------------------------
 * Private portal cards (the full set for confirmed guests)
 * ----------------------------------------------------------- */
export const PRIVATE_CARDS = [
  { key: "provisiones",    icon: "basket-shopping",      label: "Antes de venir",    body: "Víveres, agua, hielo — la tienda cercana no basta.",     bg: PASTEL_TERRA, accent: ACCENT_TERRA, critical: true },
  { key: "estancia",       icon: "clock",                label: "Tu estancia",       body: "Check-in 3pm, check-out 12pm, llegada máx 8pm.",         bg: PASTEL_TERRA, accent: ACCENT_TERRA },
  { key: "llegar",         icon: "map-location-dot",     label: "Cómo llegar",       body: "Ruta, referencias, qué hacer si llovió.",                bg: PASTEL_MAR,   accent: ACCENT_MAR   },
  { key: "amenidades",     icon: "house-chimney",        label: "Amenidades",        body: "Lo que encontrarás en la casa.",                         bg: PASTEL_SAGE,  accent: ACCENT_SAGE  },
  { key: "lugar",          icon: "water",                label: "Descubre el lugar", body: "Playa, asoleadero, fogata, río.",                        bg: PASTEL_MAR,   accent: ACCENT_MAR   },
  { key: "historia",       icon: "heart",                label: "La historia",       body: "El recuerdo que dio origen.",                            bg: PASTEL_TERRA, accent: ACCENT_TERRA },
  { key: "consejos",       icon: "lightbulb",            label: "Cómo disfrutar",    body: "Pequeños consejos para vivirlo mejor.",                  bg: PASTEL_SAND,  accent: ACCENT_GOLD  },
  { key: "naturaleza",     icon: "leaf",                 label: "Naturaleza",        body: "Mosquitos, viento, electricidad — con contexto.",        bg: PASTEL_SAGE,  accent: ACCENT_SAGE  },
  { key: "asador",         icon: "fire",                 label: "Reserva del asador", body: "Solicita tu horario por WhatsApp.",                     bg: PASTEL_TERRA, accent: ACCENT_TERRA },
  { key: "reglamento",     icon: "scroll",               label: "Convivencia",       body: "Las pocas reglas de la casa.",                           bg: PASTEL_MAR,   accent: ACCENT_MAR   },
  { key: "faq",            icon: "circle-question",      label: "Preguntas frecuentes", body: "Supermercado, hielo, pescado, luz, río.",             bg: PASTEL_SAND,  accent: ACCENT_GOLD  },
  { key: "recomendaciones", icon: "utensils",            label: "Recomendaciones",   body: "Comida en casa, 5 lugares en Tuxpan, mariscos y provisiones.", bg: PASTEL_SAGE,  accent: ACCENT_SAGE  },
  { key: "aquapark",       icon: "person-swimming",       label: "Parque acuático",   body: "AquaPark Tuxpan — a pocos minutos por el camino de playa.",   bg: PASTEL_MAR,   accent: ACCENT_MAR   },
  { key: "recuerdos",      icon: "camera",               label: "Comparte tus recuerdos", body: "Instagram y el hashtag oficial.",                   bg: PASTEL_SAND,  accent: ACCENT_GOLD  },
  { key: "antes",          icon: "star",                 label: "Antes de irte",     body: "Cómo dejar la casa y ayudarnos con una reseña.",         bg: PASTEL_TERRA, accent: ACCENT_TERRA },
];

/* -----------------------------------------------------------
 * Section hero images — inline within each section body.
 * Sparingly used: only in emotionally narrative sections.
 * ----------------------------------------------------------- */
export const SECTION_HERO = {
  llegar:     { src: "/experiencia/arco.jpg",              caption: "El arco de entrada — cuando lo veas, ya llegaste." },
  historia:   { src: "/experiencia/atardecer-palmeras.jpg", caption: "El atardecer que dio origen a este lugar." },
  lugar:      { src: "/experiencia/playa-lejos.jpg",       caption: "Barra de Galindo — una playa que aún se puede recorrer sin encontrarse a nadie." },
  naturaleza: { src: "/experiencia/arbol-atardecer.jpg",   caption: "El árbol al atardecer — de las estampas más queridas del lugar." },
  estancia:   { src: "/experiencia/orilla.jpg",            caption: "Los primeros pasos en la orilla." },
  antes:      { src: "/experiencia/brazos-atardecer.jpg",  caption: "Hasta el próximo viaje." },
};

/* -----------------------------------------------------------
 * Amenidades — inventario editorial (público + privado)
 * ----------------------------------------------------------- */
export const AMENIDADES = {
  eyebrow: "AMENIDADES",
  title: "Lo que encontrarás en la casa.",
  intro: "Todo pensado para que llegues, respires y no tengas que pensar en nada más.",
  groups: [
    {
      icon: "temperature-arrow-down",
      label: "Confort",
      items: [
        "Aire acondicionado en toda la casa",
        "Sala amplia y comedor",
        "Pantalla inteligente y WiFi",
        "Recámaras con camas matrimoniales y literas",
      ],
    },
    {
      icon: "utensils",
      label: "Cocina equipada",
      items: [
        "Refrigerador, estufa y microondas",
        "Cafetera, licuadora y utensilios",
        "Platos, vasos, tazas y cubiertos",
        "Área para preparar la comida con calma",
      ],
    },
    {
      icon: "shower",
      label: "Baños completos",
      items: [
        "2 baños con agua caliente",
        "Ducha con buena presión",
        "Toallas para cada huésped",
      ],
    },
    {
      icon: "umbrella-beach",
      label: "Vida al aire libre",
      items: [
        "Asador con utensilios",
        "Palapa de playa con hamacas",
        "Camastros, mesas y sillas para playa",
        "Área exclusiva para fogata",
      ],
    },
  ],
};

/* -----------------------------------------------------------
 * Estancia — datos operativos (solo portal privado)
 * ----------------------------------------------------------- */
export const ESTANCIA = {
  eyebrow: "TU ESTANCIA",
  title: "Lo esencial de tu llegada.",
  items: [
    {
      label: "Check-in",
      value: "3:00 pm",
      note: "Con flexibilidad — avísanos por WhatsApp si necesitas ajuste.",
    },
    {
      label: "Check-out",
      value: "12:00 pm",
      note: "Con flexibilidad — pregúntanos si quieres extender un poco.",
    },
    {
      label: "Hora máxima de llegada",
      value: "8:00 pm",
      note: "Hora recomendada. Después de esa hora, el acceso de los últimos 10 minutos —que es solo por playa— se vuelve complicado. Si algo se atraviesa en el camino, contáctanos.",
    },
    {
      label: "Registro",
      value: "Identifícate",
      note: "Al llegar, la persona que realizó la reservación deberá dar su nombre completo a los encargados.",
    },
    {
      label: "Estacionamiento",
      value: "Lugar asignado",
      note: "Al llegar se asignará el lugar para tu automóvil.",
    },
    {
      label: "WiFi",
      value: "pura_vida · galindo2023",
      note: "Red: pura_vida · Contraseña: galindo2023. Óptimo para trabajo y mensajes de día; por la noche puede ir más lento.",
    },
  ],
};

/* -----------------------------------------------------------
 * Reglamento / Convivencia (solo portal privado)
 * Tono: claro, no punitivo. Agrupado en 4 pilares.
 * ----------------------------------------------------------- */
export const REGLAMENTO = {
  eyebrow: "CONVIVENCIA",
  title: "Las pocas reglas de la casa.",
  intro: "El conjunto es un ambiente familiar. Estas son las pocas reglas que nos permiten cuidarlo entre todos.",
  groups: [
    {
      icon: "clock",
      label: "Horarios que respetamos",
      rules: [
        "Check-in a partir de las 3:00 pm.",
        "Check-out a las 12:00 pm.",
        "Fogata a partir de las 8:00 pm.",
        "Mobiliario de playa en servicio de 8:00 am a 7:00 pm.",
        "Si compartes palapas con otros huéspedes, hasta las 10:00 pm — después puedes seguir en la zona de playa.",
      ],
    },
    {
      icon: "heart",
      label: "Un ambiente familiar",
      rules: [
        "No se permiten fiestas ni eventos.",
        "No se permite realizar rituales ni ceremonias dentro de la propiedad.",
        "Sí se admiten mascotas — hasta 4. Las razas grandes duermen fuera de la casa o loft.",
        "Razas pequeñas o medianas pueden entrar siguiendo estos lineamientos: llevar sus propios trastes de alimento y bebida, cama, cobijas y toallas; no pueden subirse a camas ni sillones; los dueños se hacen responsables de recoger sus desechos y no pueden dejarlas sueltas solas — si van sin correa, debe ser acompañadas por su dueño.",
        "Cualquier daño o desperfecto ocasionado por mascotas deberá ser cubierto en su totalidad por el huésped.",
        "Se puede disfrutar una copa o cerveza; no se permite fumar ni tomar en exceso dentro de la casa.",
        "Si alguna persona llega en estado de ebriedad, se le negará el acceso.",
        "Villas Pura Vida es para vacacionar y descansar. Si buscas otro tipo de festejo, no somos tu opción.",
      ],
    },
    {
      icon: "leaf",
      label: "Cuidar el lugar",
      rules: [
        "Todos los objetos de la casa se usan solo durante la estancia.",
        "Si algo se maltrata, se rompe o se pierde, se cubre el costo — solo pedimos avisarnos de inmediato para resolverlo de la mejor manera.",
        "No dejar basura en la playa ni en las áreas comunes.",
        "Sacar la basura diariamente al área asignada.",
        "Respetar el área exclusiva de fogata.",
        "Respetar el estacionamiento asignado.",
        "No mover muebles al exterior ni sacar blancos (sábanas, toallas) de la propiedad.",
      ],
    },
    {
      icon: "users",
      label: "Áreas compartidas",
      rules: [
        "El conjunto tiene otras casas independientes. Tu alojamiento es exclusivo, pero palapas, asador y jardín se comparten cuando hay más huéspedes.",
        "El asador funciona con reserva — solicita tu horario por WhatsApp al llegar.",
        "Mobiliario de playa: ustedes lo suben y bajan. No dejarlo solo en la playa.",
        "Solo pueden hospedarse las personas registradas en la reserva. Para visitantes o huéspedes adicionales, consúltanos antes.",
      ],
    },
    {
      icon: "leaf",
      label: "Uso responsable de recursos",
      rules: [
        "Aire acondicionado: mantén puertas y ventanas cerradas cuando esté funcionando. Apágalo al salir.",
        "Agua: Barra de Galindo es una zona donde el agua es un recurso valioso. Úsala con responsabilidad.",
        "Cocina: no dejar alimentos perecederos al finalizar la estancia; lavar utensilios o dejarlos ordenados.",
      ],
    },
    {
      icon: "life-ring",
      label: "Si algo pasa",
      rules: [
        "Cualquier daño o desperfecto: avísanos de inmediato — resolvemos juntos.",
        "Objetos olvidados: escríbenos lo antes posible. Buscamos y coordinamos el envío (paquetería a cargo del huésped).",
        "Emergencias: contacta al anfitrión por WhatsApp; en el portal encuentras teléfonos, hospitales, farmacias y gasolineras cercanas.",
        "Fogata: siempre supervisada por un adulto; solo leña o carbón (nunca gasolina); apagar completamente antes de retirarse.",
      ],
    },
  ],
  golden: "El respeto a la casa, al conjunto y a los otros huéspedes es lo que hace posible que este lugar exista.",
};

/* -----------------------------------------------------------
 * Antes de salir — checklist de check-out
 * ----------------------------------------------------------- */
export const ANTES_DE_SALIR = [
  { icon: "lightbulb", label: "Apagar luces." },
  { icon: "temperature-arrow-down", label: "Apagar el aire acondicionado." },
  { icon: "door-closed", label: "Cerrar puertas y ventanas." },
  { icon: "magnifying-glass", label: "Verificar que no olviden objetos personales." },
  { icon: "chair", label: "Regresar el mobiliario de playa a su lugar." },
  { icon: "trash", label: "Dejar la basura en el área asignada." },
  { icon: "whatsapp", label: "Avisarnos por WhatsApp cuando hayan salido." },
  { icon: "star", label: "Si disfrutaste tu estancia, ayúdanos con una reseña — significa muchísimo." },
];

/* -----------------------------------------------------------
 * Pet Friendly — manifiesto obligatorio para huéspedes con mascota.
 * Se muestra en sección roja crítica del portal y debe aceptarse
 * antes del check-in. Si T-1 no está aceptado, se envía alerta al
 * dueño automáticamente (scheduler_service.py).
 * ----------------------------------------------------------- */
export const PET_MANIFIESTO = {
  eyebrow: "MASCOTAS · IMPORTANTE",
  title: "Manifiesto de convivencia con mascotas.",
  intro: "Villas Pura Vida es pet-friendly con reglas claras. Antes del check-in te pedimos leer y aceptar este manifiesto — es lo que nos permite recibir a tu mascota y a las de otras familias con tranquilidad.",
  criticalNote: "Aceptar este manifiesto es obligatorio para poder ingresar con tu mascota. Si tienes dudas, escríbenos por WhatsApp antes de aceptar.",
  rules: [
    {
      icon: "paw",
      title: "Hasta 4 mascotas por reserva",
      body: "Bienvenidas hasta 4 mascotas en total. Nos avisas con anticipación cuántas vienen y de qué tamaño para prepararte todo mejor.",
    },
    {
      icon: "moon",
      title: "Razas grandes duermen fuera",
      body: "Por respeto al mobiliario, blancos y otros huéspedes, las razas grandes duermen en el exterior de la casa o loft.",
    },
    {
      icon: "suitcase",
      title: "Trae sus cosas",
      body: "Llevar sus propios trastes de alimento y agua, su cama, cobijas y toallas. Todo lo que la haga sentir en casa.",
    },
    {
      icon: "bed",
      title: "No suben a camas ni sillones",
      body: "Aunque en casa sea lo normal, aquí las mascotas no pueden subirse a camas, sillones ni sofás. Es una regla firme.",
    },
    {
      icon: "person-walking",
      title: "Nunca sueltas y sin supervisión",
      body: "Recogemos siempre sus desechos. No dejamos mascotas sueltas ni solas — si van sin correa, deben ir acompañadas por su dueño en todo momento.",
    },
    {
      icon: "hand-holding-dollar",
      title: "Daños se cubren al 100%",
      body: "Cualquier daño o desperfecto ocasionado por la mascota (mobiliario, blancos, jardín, otras propiedades) es cubierto en su totalidad por el huésped.",
    },
  ],
  acceptedLabel: "Manifiesto aceptado",
  acceptButtonLabel: "Acepto las reglas de mascotas",
  acceptSuccessMessage: "¡Gracias! Ya podemos recibir a tu mascota con tranquilidad.",
};

/* -----------------------------------------------------------
 * Comunicación — timeline de mensajes que recibirá el huésped
 * (transparencia total: qué esperar y cuándo)
 * Los textos son los oficiales del Manual Airbnb — misma copia
 * que enviaremos por WhatsApp / email cuando llegue el momento.
 * ----------------------------------------------------------- */
export const COMUNICACION = {
  eyebrow: "QUÉ ESPERAR DE NOSOTROS",
  title: "Te acompañamos durante todo el viaje.",
  intro: "Desde el momento en que reservas hasta después del check-out, recibirás mensajes claros y cercanos por WhatsApp. Nunca automáticos — siempre pensados para ti.",
  steps: [
    {
      key: "al_reservar",
      moment: "Al reservar",
      label: "Bienvenida cálida",
      body: "Confirmamos la reserva y te anticipamos que en los próximos días llegarán recomendaciones y guía de llegada.",
      message: [
        "Hola, {Nombre}.",
        "*¡Gracias por elegir Villas Pura Vida!* 🌊",
        "Nos da muchísimo gusto que hayan decidido pasar unos días con nosotros. Queremos que desde este momento solo se preocupen por disfrutar su viaje.",
        "En los próximos días les enviaremos información importante sobre cómo llegar, recomendaciones para preparar su estancia y algunos consejos que harán su llegada mucho más sencilla.",
        "Si durante la planeación surge cualquier duda, con gusto estamos para ayudarles.",
        "*¡Será un placer recibirlos!*",
      ],
    },
    {
      key: "bienvenida_portal",
      moment: "Después de confirmar",
      label: "Acceso al Portal del Huésped",
      body: "Enviamos el enlace privado del Portal del Huésped con toda la información de su estancia.",
      message: [
        "Hola, *{Nombre}*.",
        "Queremos darles la bienvenida oficial a *Villas Pura Vida*. 🌅",
        "Les compartimos su acceso al *Portal del Huésped* — un espacio privado donde encontrarán todo lo necesario para preparar y disfrutar su estancia:",
        "✔ Guía de llegada con referencias del camino.\n✔ Qué traer desde casa (víveres, hielo, repelente, etc.)\n✔ La historia del lugar, amenidades y reglas de convivencia\n✔ WiFi, horarios, recomendaciones locales y contactos",
        "🔗 Su enlace personal: {PortalLink}",
        "*¡ES MUY IMPORTANTE LEER TODA LA INFORMACIÓN!*",
        "_Consérvenlo — pueden entrar cuando quieran, desde cualquier dispositivo. Si algo no queda claro, escríbanme, estoy para servirles._",
      ],
    },
    {
      key: "5_dias_antes",
      moment: "5 días antes",
      label: "Preparativos",
      body: "Recomendaciones de compras (víveres, hielo, carbón), qué traer si vienes con niños, y opciones si quieres hacer una carne asada.",
      message: [
        "Hola, {Nombre}. Ya falta muy poco para su viaje. 🌴",
        "Queremos compartir algunas recomendaciones que normalmente hacen la estancia mucho más cómoda:",
        "✔ Si vienen de ciudades cercanas, les recomendamos hacer sus compras antes de llegar (es una zona semivirgen; la tienda más cercana está a unos 30 minutos).",
        "✔ Si viajan con niños, no olviden protector solar, repelente y juguetes de playa.",
        "✔ Si desean hacer una carne asada, pueden traer carbón y utensilios — el asador funciona con reserva, coordinaremos horario al llegar.",
        "En unos días también les enviaremos la ubicación exacta y las indicaciones para llegar sin complicaciones.",
      ],
    },
    {
      key: "1_dia_antes",
      moment: "1 día antes",
      label: "Guía de llegada",
      body: "Ubicación exacta, video del recorrido, referencias del camino y qué comprar antes de tomar la playa.",
      message: [
        "Hola, {Nombre}. ¡Mañana comienza su descanso! 🚗",
        "Los últimos minutos del recorrido se realizan por la playa. No se preocupen, es completamente normal y forma parte de la experiencia de llegar a Barra de Galindo. Les compartimos la ubicación y un video con el recorrido.",
        "Antes de llegar les sugerimos comprar: agua, hielo, alimentos, botanas, carbón si utilizarán el asador, repelente y protector solar.",
        "Recuerden que la hora máxima de llegada es a las 8 pm — después de esa hora no hay quien reciba. Si durante el camino tienen alguna duda, pueden llamarnos.",
      ],
    },
    {
      key: "dia_llegada",
      moment: "Día de llegada",
      label: "Bienvenida al lugar",
      body: "Recomendaciones para mosquitos, playa, mobiliario, fogata y asador. Contactos de emergencia.",
      message: [
        "¡Bienvenidos a Villas Pura Vida! 🌅 Esperamos que disfruten muchísimo estos días.",
        "🦟 Mosquitos: al estar en una zona natural puede haber mosquitos al amanecer y al atardecer. Usen repelente si permanecerán en exteriores.",
        "🏖️ Playa: es pública y semivirgen. Disfrútenla con responsabilidad y cuiden a los niños.",
        "🔥 Mobiliario, fogata y asador: al terminar de usarlos, déjenlos en su lugar y limpios para el siguiente huésped. Fogata a partir de las 8 pm, mobiliario de playa 8 am a 7 pm.",
        "Si necesitan cualquier cosa, escríbannos. Con gusto les ayudaremos.",
      ],
    },
    {
      key: "segundo_dia",
      moment: "Segundo día",
      label: "¿Todo bien?",
      body: "Un mensaje breve para asegurarnos de que todo esté funcionando. Sin invadir, solo por si necesitas algo.",
      message: [
        "Hola, {Nombre}.",
        "*Esperamos que estén disfrutando mucho su estancia.* 🌊 🥥 🏝",
        "Solo queremos asegurarnos de que todo esté funcionando correctamente.",
        "*¿Hay algo en lo que podamos ayudarles?*",
      ],
    },
    {
      key: "1_dia_antes_checkout",
      moment: "1 día antes del check-out",
      label: "Recordatorios",
      body: "Checklist antes de salir: luces, aire, ventanas, objetos personales.",
      message: [
        "Esperamos que hayan disfrutado muchísimo estos días. Antes de su salida les compartimos algunos recordatorios:",
        "✔ Revisar que no olviden objetos personales.",
        "✔ Apagar luces y aire acondicionado.",
        "✔ Cerrar puertas y ventanas.",
        "Cuando salgan, envíennos un mensaje para saber que el alojamiento quedó disponible. Muchas gracias.",
      ],
    },
    {
      key: "post_checkout",
      moment: "Después del check-out",
      label: "Gracias",
      body: "Un mensaje sincero de agradecimiento e invitación a compartir su viaje en redes y dejar una reseña.",
      message: [
        "Muchas gracias por haber elegido *Villas Pura Vida*. 🙏",
        "Esperamos que este viaje haya dejado grandes recuerdos para ustedes y su familia.",
        "Nos encantaría ver cómo lo vivieron. *Compartan sus mejores fotos y su experiencia en redes — etiquétennos:*",
        "📸 Instagram: @villaspuravida.tuxpan\nhttps://www.instagram.com/villaspuravida.tuxpan/\n\n👍 Facebook: Villas Pura Vida https://www.facebook.com/share/1BkkG4kNE2/\n\n🎵 TikTok: @villas.pura.vida https://www.tiktok.com/@villas.pura.vida\n\nHashtag: #VillasPuraVida",
        "Y si disfrutaron su estancia, una reseña nos ayudaría muchísimo a que más familias descubran este lugar.",
        "*Esperamos volver a recibirlos muy pronto.* 🌅",
      ],
    },
  ],
};
